/**
 * BlurOverlay Component
 * Visual and functional barrier that prevents access to partner-only content.
 * Uses CommonModal for consistent structure and styling with other app popups.
 */
'use client';

import React from 'react';
import { safeRedirect } from '@/helpers/safe-redirect';
import { CommonModal } from '@/components/common/modal';
import { DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ArrowRight } from '@/components/Icons/Icons';

interface ButtonConfig {
  id: string;
  title: string;
  variant?: 'primary' | 'secondary';
  onClick?: () => void;
  redirectPath?: string;
}

interface BlurOverlayProps {
  open: boolean;
  message: React.ReactNode | string;
  buttons: ButtonConfig[];
  onOpenChange?: (open: boolean) => void;
}

const PARTNER_ONLY_TITLE = 'Partner-only content';

export default function BlurOverlay({ open, message, buttons, onOpenChange }: BlurOverlayProps) {
  const handleButtonClick = (button: ButtonConfig) => {
    if (button.onClick) {
      button.onClick();
    } else if (button.redirectPath) {
      safeRedirect(button.redirectPath);
    }
  };

  return (
    <CommonModal
      open={open}
      onOpenChange={onOpenChange ?? (() => {})}
      showCloseButton={false}
      className="sm:max-w-[540px]"
    >
      {/* Single bg and layout like Forgot password popup – no separate header/footer */}
      <div className="login-modal-container rounded-lg">
        <div className="login-modal-content">
          <DialogTitle className="cs-h3 tab:h1-compact mb-2">
            {PARTNER_ONLY_TITLE}
          </DialogTitle>
          {typeof message === 'string' ? (
            <p className="cs-s-body-light text-text-default-medium mb-7.5">{message}</p>
          ) : (
            <div className="cs-s-body-light text-text-default-medium mb-7.5 space-y-3 text-left">
              {message}
            </div>
          )}

          <div className="flex flex-col gap-2">
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 justify-center">
              {buttons.map((button) => (
                <Button
                  key={button.id}
                  onClick={() => handleButtonClick(button)}
                  variant={button.variant === 'secondary' ? 'SecondaryFillButton' : 'PrimaryFillButton'}
                  className="w-full sm:w-auto sm:flex-1 cs-button touch-manipulation"
                >
                  {button.title}
                  {button.variant !== 'secondary' && (
                    <ArrowRight className="text-text-secondary" width={12} height={10} />
                  )}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </CommonModal>
  );
}

/**
 * Default message content for partner-only overlay (for use as `message` prop).
 */
export function PartnerOnlyMessage() {
  return (
    <>
      <p className="cs-m-body text-text-secondary mb-0">
        This learning material is exclusively for Contentstack Partners. Please log in using your Partner account to access this content.
      </p>
      <p className="cs-m-body text-text-secondary">
        If you&apos;re not a partner, you can still explore other resources available to everyone in Contentstack Academy.
      </p>
    </>
  );
}